import { useState } from 'react';
import { Button, Card, Divider, Icon } from '@nike/eds';
import { useMutation } from '@tanstack/react-query';
import { useRevalidator } from 'react-router-dom';

import consumerProductProcessorClient from '../../clientAPI/consumerproductprocessorv1';

type ProductIdType = 'globalProductId' | 'productCode';
type ResultState = { success: boolean; message: string } | null;

function ResultBadge({ result }: { result: ResultState }) {
  if (!result) return null;
  return (
    <span
      className={`tw-text-sm tw-font-medium ${
        result.success ? 'tw-text-green-600' : 'tw-text-red-600'
      }`}
    >
      {result.message}
    </span>
  );
}

const ID_TYPE_OPTIONS: { value: ProductIdType; label: string }[] = [
  { value: 'productCode', label: 'Product Code' },
  { value: 'globalProductId', label: 'Global Product ID' },
];

export default function ForceUpdate() {
  const revalidator = useRevalidator();

  const [idType, setIdType] = useState<ProductIdType>('productCode');
  const [productId, setProductId] = useState('');
  const [consumerChannelId, setConsumerChannelId] = useState('');
  const [result, setResult] = useState<ResultState>(null);

  const forceUpdateByGlobalProductIdMutation = useMutation({
    mutationFn: () =>
      consumerProductProcessorClient.forceUpdateByQuery({
        globalProductId: productId,
        consumerChannelId,
      }),
    onSuccess: () => setResult({ success: true, message: 'Success' }),
    onError: (err: any) => {
      setResult({
        success: false,
        message: `Error: ${err.message || 'Request failed'}`,
      });
      if (err.status === 401) revalidator.revalidate();
    },
  });

  const forceUpdateByProductCodeMutation = useMutation({
    mutationFn: () =>
      consumerProductProcessorClient.forceUpdateByQuery({
        productCode: productId,
        consumerChannelId,
      }),
    onSuccess: () => setResult({ success: true, message: 'Success' }),
    onError: (err: any) => {
      setResult({
        success: false,
        message: `Error: ${err.message || 'Request failed'}`,
      });
      if (err.status === 401) revalidator.revalidate();
    },
  });

  const isLoading =
    forceUpdateByGlobalProductIdMutation.isLoading ||
    forceUpdateByProductCodeMutation.isLoading;

  const handleSubmit = () => {
    setResult(null);
    if (idType === 'globalProductId') {
      forceUpdateByGlobalProductIdMutation.mutate();
    } else {
      forceUpdateByProductCodeMutation.mutate();
    }
  };

  return (
    <div className='tw-w-10/12 tw-mx-auto tw-py-10'>
      <div className='tw-mb-8'>
        <p className='eds-type--display-4'>Force Update</p>
        <p className='tw-text-sm tw-text-gray-600 tw-mt-1'>
          Trigger consumer product processor force updates
        </p>
      </div>

      <Card className='tw-p-6'>
        <div className='tw-flex tw-flex-col tw-gap-4 tw-max-w-lg'>
          <div>
            <label
              htmlFor='consumerChannelId'
              className='tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-1'
            >
              Consumer Channel ID <span className='tw-text-red-600'>*</span>
            </label>
            <select
              id='consumerChannelId'
              value={consumerChannelId}
              onChange={(e) => setConsumerChannelId(e.target.value)}
              className='tw-w-full tw-px-3 tw-py-2 tw-border-0 tw-border-b-2 tw-border-gray-300 tw-bg-transparent focus:tw-outline-none focus:tw-border-blue-500'
            >
              <option value=''>All</option>
              <option value='82a74ac1-c527-4470-b7b0-fb5f3ef3c2e2'>
                Nike App
              </option>
              <option value='d9a5bc42-4b9c-4976-858a-f159cf99c647'>
                Nike.COM
              </option>
              <option value='15d92135-394e-487a-8afa-f6c8525c9ea9'>
                WeChat
              </option>
              <option value='008be467-6c78-4079-94f0-70e2d6cc4003'>
                SNKRS
              </option>
            </select>
          </div>

          <div>
            <div className='tw-flex tw-items-center tw-gap-3 tw-mb-1'>
              <select
                value={idType}
                onChange={(e) => {
                  setIdType(e.target.value as ProductIdType);
                  setProductId('');
                  setResult(null);
                }}
                className='tw-px-2 tw-py-1 tw-border-0 tw-border-b-2 tw-border-gray-300 tw-bg-transparent tw-text-sm tw-font-medium tw-text-gray-700 focus:tw-outline-none focus:tw-border-blue-500'
              >
                {ID_TYPE_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
              <span className='tw-text-red-600 tw-text-sm'>*</span>
            </div>
            <div className='tw-flex tw-gap-3 tw-items-center'>
              <input
                type='text'
                value={productId}
                onChange={(e) => setProductId(e.target.value)}
                placeholder={
                  idType === 'globalProductId'
                    ? 'e.g. ca85b990-22a9-361f-8787-103232afa13e'
                    : 'e.g. DD1391-100'
                }
                className='tw-flex-1 tw-px-3 tw-py-2 tw-border-0 tw-border-b-2 tw-border-gray-300 tw-bg-transparent focus:tw-outline-none focus:tw-border-blue-500'
              />
              <Button
                size='medium'
                variant='primary'
                onClick={handleSubmit}
                disabled={!productId.trim() || isLoading}
                beforeSlot={isLoading ? undefined : <Icon name='Refresh' />}
              >
                {isLoading ? 'Submitting...' : 'Update'}
              </Button>
            </div>
            <div className='tw-mt-2'>
              <ResultBadge result={result} />
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
}
